import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calculator, ShoppingBag, Trash2, X, ChevronDown, ChevronUp, Wifi, WifiOff, RefreshCw, Eye } from 'lucide-react';
import { useProductos } from '../utils/useProductos';
import api from '../utils/api';
import { extraerUnidad } from '../utils/unidades';
import {
  getLocalCalculosByProject, saveLocalCalculo, deleteLocalCalculo,
  setProjectCalculos, isOnline, onConnectionChange, syncPendingCalculos,
  getSyncQueue
} from '../utils/offlineStorage';

const PI = Math.PI;
const calcRendimiento = (micras, solidos, thinner) =>
  (1000 / micras) * (parseFloat(solidos) || 1) * (1 + (parseFloat(thinner) || 0));

const CATEGORIAS = [
  { id:'superficies_planas', label:'Superficies planas', tipos:[
    {id:'rectangulo',label:'Rectangulo / Pared / Piso'},
    {id:'triangulo',label:'Triangulo'},
    {id:'trapezoide',label:'Trapezoide'},
    {id:'circulo',label:'Circulo / Disco'},
    {id:'sector_circular',label:'Sector circular'},
    {id:'elipse',label:'Elipse'},
  ]},
  { id:'cisternas', label:'Cisternas', tipos:[
    {id:'cisterna_rect_interior',label:'Cisterna rect. — Interior'},
    {id:'cisterna_rect_paredes',label:'Cisterna rect. — Paredes'},
    {id:'cisterna_rect_exterior',label:'Cisterna rect. — Exterior'},
  ]},
  { id:'tanques', label:'Tanques', tipos:[
    {id:'tanque_cilindrico',label:'Tanque cilindrico'},
    {id:'tanque_rect',label:'Tanque rectangular'},

    {id:'tanque_esferico',label:'Tanque esferico (desde litros)'},
    {id:'tanque_cono',label:'Tanque conico'},
    {id:'anillo_base_tanque',label:'Anillo base tanque'},
  ]},
  { id:'tuberias', label:'Tuberias', tipos:[
    {id:'tuberia_exterior',label:'Tuberia exterior (metros)'},
    {id:'tuberia_pies',label:'Tuberia exterior (pies)'},

  ]},

];

const INPUTS_DEF = {
  rectangulo:             [{k:'largo',l:'Largo (m)'},{k:'ancho',l:'Ancho (m)'},{k:'cantidad',l:'N de piezas iguales',def:1}],
  triangulo:              [{k:'base',l:'Base (m)'},{k:'altura',l:'Altura (m)'},{k:'cantidad',l:'N de piezas',def:1}],
  trapezoide:             [{k:'l1',l:'Lado largo L1 (m)'},{k:'l2',l:'Lado corto L2 (m)'},{k:'altura',l:'Altura H (m)'},{k:'cantidad',l:'N de piezas',def:1}],
  circulo:                [{k:'diametro',l:'Diametro (m)'},{k:'cantidad',l:'N de piezas',def:1}],
  sector_circular:        [{k:'radio',l:'Radio (m)'},{k:'angulo',l:'Angulo (grados)'}],
  elipse:                 [{k:'a',l:'Semi-eje A (m)'},{k:'b',l:'Semi-eje B (m)'},{k:'cantidad',l:'N de piezas',def:1}],
  cisterna_rect_interior: [{k:'largo',l:'Largo interior (m)'},{k:'ancho',l:'Ancho interior (m)'},{k:'alto',l:'Alto interior (m)'},{k:'conTecho',l:'Incluir techo',tipo:'check',def:true}],
  cisterna_rect_paredes:  [{k:'largo',l:'Largo (m)'},{k:'ancho',l:'Ancho (m)'},{k:'alto',l:'Alto (m)'}],
  cisterna_rect_exterior: [{k:'largo',l:'Largo ext. (m)'},{k:'ancho',l:'Ancho ext. (m)'},{k:'alto',l:'Alto ext. (m)'}],
  tanque_cilindrico:      [{k:'diametro',l:'Diametro (m)'},{k:'largo',l:'Largo / Altura (m)'},{k:'espejos',l:'N de tapas',def:2}],
  tanque_rect:            [{k:'largo',l:'Largo (m)'},{k:'ancho',l:'Ancho (m)'},{k:'alto',l:'Alto (m)'},{k:'interior',l:'Interior',tipo:'check',def:true}],

  tanque_esferico:        [{k:'radio',l:'Radio (m)'},{k:'cantidad',l:'N de piezas',def:1}],
  tanque_cono:            [{k:'radio',l:'Radio base (m)'},{k:'altura',l:'Altura (m)'},{k:'conBase',l:'Incluir base',tipo:'check',def:true}],
  anillo_base_tanque:     [{k:'diametro',l:'Diametro del tanque (m)'},{k:'anchoAnillo',l:'Ancho del anillo (m)',def:0.20},{k:'anchoImper',l:'Extension impermeabilizante (m)',def:0.10}],
  tuberia_exterior:       [{k:'diametro_pulg',l:'Diametro (pulgadas)'},{k:'largo',l:'Largo (metros)'}],
  tuberia_pies:           [{k:'diametro_pulg',l:'Diametro (pulgadas)'},{k:'largo_pies',l:'Largo (pies)'}],


};

function nv(v,k,def=0){return parseFloat(v[k]??def)||0;}

function calcArea(tipo,v){
  let area=0,detalles=[],extra=null;
  switch(tipo){
    case 'rectangulo': area=nv(v,'largo')*nv(v,'ancho')*nv(v,'cantidad',1); detalles=[`${nv(v,'largo')} x ${nv(v,'ancho')} x ${nv(v,'cantidad',1)} = ${area.toFixed(4)} m2`]; break;
    case 'triangulo': area=(nv(v,'base')*nv(v,'altura')/2)*nv(v,'cantidad',1); detalles=[`(base x altura)/2 x cant = ${area.toFixed(4)} m2`]; break;
    case 'trapezoide': area=(nv(v,'altura')*(nv(v,'l1')+nv(v,'l2'))/2)*nv(v,'cantidad',1); detalles=[`H x (L1+L2)/2 x cant = ${area.toFixed(4)} m2`]; break;
    case 'circulo': {const r=nv(v,'diametro')/2; area=PI*r*r*nv(v,'cantidad',1); detalles=[`pi x R2 x cant = ${area.toFixed(4)} m2`]; break;}
    case 'sector_circular': {const r=nv(v,'radio'),a=nv(v,'angulo'); area=(PI*r*r*a)/360; detalles=[`(pi x R2 x alfa)/360 = ${area.toFixed(4)} m2`]; break;}
    case 'elipse': area=PI*nv(v,'a')*nv(v,'b')*nv(v,'cantidad',1); detalles=[`pi x A x B x cant = ${area.toFixed(4)} m2`]; break;
    case 'cisterna_rect_interior': {const l=nv(v,'largo'),an=nv(v,'ancho'),h=nv(v,'alto'),par=2*(l+an)*h,piso=l*an,techo=v.conTecho!==false?l*an:0; area=par+piso+techo; detalles=[`Paredes=${par.toFixed(4)}`,`Piso=${piso.toFixed(4)}`,v.conTecho!==false?`Techo=${techo.toFixed(4)}`:'Sin techo',`TOTAL=${area.toFixed(4)} m2`]; break;}
    case 'cisterna_rect_paredes': {const l=nv(v,'largo'),an=nv(v,'ancho'),h=nv(v,'alto'); area=2*(l+an)*h; detalles=[`2 x (L+A) x H = ${area.toFixed(4)} m2`]; break;}
    case 'cisterna_rect_exterior': {const l=nv(v,'largo'),an=nv(v,'ancho'),h=nv(v,'alto'); area=2*((l*an)+(l*h)+(an*h)); detalles=[`Exterior = ${area.toFixed(4)} m2`]; break;}
    case 'tanque_cilindrico': {const d=nv(v,'diametro'),r=d/2,L=nv(v,'largo'),esp=nv(v,'espejos',2),aE=esp*PI*r*r,aC=2*PI*r*L; area=aE+aC; detalles=[`Lateral=${aC.toFixed(4)}`,`Tapas=${aE.toFixed(4)}`,`TOTAL=${area.toFixed(4)} m2`]; break;}
    case 'tanque_rect': {const l=nv(v,'largo'),an=nv(v,'ancho'),h=nv(v,'alto'),ft=2*(l*an),cA=2*(l*h),cB=2*(an*h); area=ft+cA+cB; detalles=[`TOTAL=${area.toFixed(4)} m2`]; break;}

    case 'tanque_esferico': {const r=nv(v,'radio'),q=nv(v,'cantidad',1); area=4*PI*r*r*q; detalles=[`Radio=${r.toFixed(4)} m`,`Area=4*pi*R2*cant=${area.toFixed(4)} m2`]; break;}
    case 'tanque_cono': {const r=nv(v,'radio'),h=nv(v,'altura'),s=Math.sqrt(r*r+h*h),lat=PI*r*s,base=v.conBase!==false?PI*r*r:0; area=lat+base; detalles=[`TOTAL=${area.toFixed(4)} m2`]; break;}
    case 'anillo_base_tanque': {const D=nv(v,'diametro'),R=D/2,aA=nv(v,'anchoAnillo',0.20),aI=nv(v,'anchoImper',0.10),rExtA=R+aA,rExtI=R+aA+aI,aAnillo=PI*(rExtA*rExtA-R*R),aImper=PI*(rExtI*rExtI-R*R),longC=2*PI*(R+aA/2)*1.10,rollos=Math.ceil(longC/10); area=aImper; detalles=[`Area anillo=${aAnillo.toFixed(4)} m2`,`Area impermeabilizante=${aImper.toFixed(4)} m2`]; extra={label:'Cinta viscoelastica',value:`${longC.toFixed(2)} m = ${rollos} rollo(s) de 10m`}; break;}
    case 'tuberia_exterior': {const dp=nv(v,'diametro_pulg'),dm=dp*0.0254,L=nv(v,'largo'); area=2*PI*(dm/2)*L; detalles=[`Area=${area.toFixed(4)} m2`]; break;}
    case 'tuberia_pies': {const dp=nv(v,'diametro_pulg'),dm=dp*0.0254,lm=nv(v,'largo_pies')*0.3048; area=2*PI*(dm/2)*lm; detalles=[`Area=${area.toFixed(4)} m2`]; break;}



    default: break;
  }
  return {area,detalles:detalles.filter(Boolean),extra};
}

export default function CalculadoraPage(){
  const { productos: PRODUCTOS } = useProductos();
  const [catSel,setCatSel]=useState('');
  const [tipoSel,setTipoSel]=useState('');
  const [vals,setVals]=useState({});
  const [resArea,setResArea]=useState(null);
  const [showDet,setShowDet]=useState(false);

  const [searchProd,setSearchProd]=useState('');
  const [prodSel,setProdSel]=useState(null);
  const [showProdList,setShowProdList]=useState(false);
  const [micras,setMicras]=useState(250);
  const [perdida,setPerdida]=useState(20);
  const [vUnitario,setVUnitario]=useState('');

  const [proyectos,setProyectos]=useState([]);
  const [proyectoSel,setProyectoSel]=useState('');
  const [calculos,setCalculos]=useState([]);
  const [showCarrito,setShowCarrito]=useState(false);
  const [saving,setSaving]=useState(false);
  const [verCalculo,setVerCalculo]=useState(null);

  // Offline state
  const [online,setOnline]=useState(isOnline());
  const [syncing,setSyncing]=useState(false);
  const [pendingCount,setPendingCount]=useState(getSyncQueue().length);

  // Track online/offline
  useEffect(()=>{
    const cleanup = onConnectionChange((status)=>{
      setOnline(status);
      if(status){
        // Back online — sync automatically
        handleSync();
      }
    });
    return cleanup;
  },[]);

  // Load projects (only when online)
  useEffect(()=>{
    if(online){
      api.get('/projects').then(r=>setProyectos(r.data)).catch(()=>{});
    } else {
      // Load project names from localStorage if cached
      try {
        const cached = localStorage.getItem('adrantaleh_proyectos');
        if(cached) setProyectos(JSON.parse(cached));
      } catch {}
    }
  },[online]);

  // Cache projects locally when loaded
  useEffect(()=>{
    if(proyectos.length>0){
      try { localStorage.setItem('adrantaleh_proyectos', JSON.stringify(proyectos)); } catch {}
    }
  },[proyectos]);

  // Load calculos — from server if online, from localStorage if offline
  useEffect(()=>{
    if(!proyectoSel){ setCalculos([]); return; }
    if(online){
      api.get(`/calculations/project/${proyectoSel}`)
        .then(r=>{
          setCalculos(r.data);
          setProjectCalculos(proyectoSel, r.data); // cache locally
        })
        .catch(()=>{
          // Fallback to local
          setCalculos(getLocalCalculosByProject(proyectoSel));
        });
    } else {
      setCalculos(getLocalCalculosByProject(proyectoSel));
    }
  },[proyectoSel,online]);

  const handleSync = async () => {
    if(!isOnline()||syncing) return;
    setSyncing(true);
    try {
      const {synced,failed} = await syncPendingCalculos(api);
      setPendingCount(getSyncQueue().length);
      if(synced>0){
        // Reload from server
        if(proyectoSel){
          const r = await api.get(`/calculations/project/${proyectoSel}`);
          setCalculos(r.data);
        }
      }
    } catch {}
    finally { setSyncing(false); }
  };

  const catActual=CATEGORIAS.find(c=>c.id===catSel);
  const tipoActual=catActual?.tipos.find(t=>t.id===tipoSel);
  const inputsDef=INPUTS_DEF[tipoSel]||[];

  const handleCat=(id)=>{setCatSel(id);setTipoSel('');setVals({});setResArea(null);};
  const handleTipo=(id)=>{setTipoSel(id);setResArea(null);const d={};(INPUTS_DEF[id]||[]).forEach(i=>{if(i.def!==undefined)d[i.k]=i.def;});setVals(d);};
  const setVal=(k,v)=>setVals(prev=>({...prev,[k]:v}));
  const handleCalc=useCallback(()=>{setResArea(calcArea(tipoSel,vals));setShowDet(false);},[tipoSel,vals]);

  const rendimiento=prodSel&&micras>0?calcRendimiento(micras,prodSel.solidos_pct||1,prodSel.thinner||0):0;
  const area=resArea?.area||0;
  const litrosBase=rendimiento>0?area/rendimiento:0;
  const litrosTot=litrosBase*(1+perdida/100);
  const galones=litrosTot/3.785;
  const litrosPres=parseFloat(prodSel?.litros)||1;
  const unidades=litrosPres>0?litrosTot/litrosPres:0;
  const unidadesCeil=Math.ceil(unidades);

  const codigosCalc=new Set(calculos.map(c=>c.productoCodigo));
  const prodFilt=PRODUCTOS.filter(p=>!searchProd||p.codigo?.toLowerCase().includes(searchProd.toLowerCase())||p.producto?.toLowerCase().includes(searchProd.toLowerCase())||p.marca?.toLowerCase().includes(searchProd.toLowerCase()));
  const prodOrd=[...prodFilt].sort((a,b)=>(codigosCalc.has(a.codigo)?0:1)-(codigosCalc.has(b.codigo)?0:1));

  const handleAgregar=async()=>{
    if(!proyectoSel){ alert('Selecciona un proyecto primero'); return; }
    if(!prodSel||!resArea||!area){ alert('Completa el calculo primero'); return; }
    setSaving(true);

    const payload = {
      projectId:proyectoSel, tipoSuperficie:tipoSel,
      superficieLabel:tipoActual?.label||tipoSel,
      inputValues:JSON.stringify(vals),
      areaM2:area, productoCodigo:prodSel.codigo,
      productoMarca:prodSel.marca, productoNombre:prodSel.producto,
      presentacion:prodSel.presentacion, micras,
      solidosPct:parseFloat(prodSel.solidos_pct)||1,
      thinnerPct:parseFloat(prodSel.thinner)||0,
      perdidaPct:perdida, rendimientoM2L:rendimiento,
      litrosBase, litrosTotales:litrosTot,
      galones, unidades:unidadesCeil, cantidad:unidadesCeil,
      unidadMedida:extraerUnidad(prodSel.presentacion),
      vUnitario:parseFloat(vUnitario)||0, enCotizacion:false,
    };

    try {
      if(online){
        // Save to server
        const res = await api.post('/calculations', payload);
        setCalculos(prev=>[...prev,res.data]);
        setProjectCalculos(proyectoSel, [...calculos, res.data]);
      } else {
        // Save locally
        const local = saveLocalCalculo(payload);
        setCalculos(prev=>[...prev,local]);
        setPendingCount(getSyncQueue().length);
        alert('Sin conexion — calculo guardado localmente. Se sincronizara cuando vuelva la conexion.');
      }
      setShowCarrito(true);
    } catch(e) {
      // Server failed — save locally as fallback
      const local = saveLocalCalculo(payload);
      setCalculos(prev=>[...prev,local]);
      setPendingCount(getSyncQueue().length);
      alert('Error de conexion — calculo guardado localmente.');
    }
    finally { setSaving(false); }
  };

  const handleEliminar=async(id)=>{
    if(!window.confirm('Eliminar este calculo?'))return;
    const isLocal = id.startsWith('local_');
    try {
      if(online && !isLocal){
        await api.delete(`/calculations/${id}`);
      }
      deleteLocalCalculo(id);
      setCalculos(prev=>prev.filter(c=>c.id!==id));
      if(!online || isLocal) setPendingCount(getSyncQueue().length);
    } catch {
      deleteLocalCalculo(id);
      setCalculos(prev=>prev.filter(c=>c.id!==id));
    }
  };

  const proyectoActual=proyectos.find(p=>p.id===proyectoSel);

  const tabStyle=(active)=>({
    padding:'7px 12px',border:'none',cursor:'pointer',fontSize:12,fontWeight:500,
    borderRadius:8,background:active?'#7B0D0D':'transparent',
    color:active?'white':'#555',transition:'all 0.15s',whiteSpace:'nowrap',
  });

  return(
    <div style={{position:'relative'}}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Calculadora</h1>
          <p className="page-subtitle">Calcula el area, elige el producto y guarda en el proyecto</p>
        </div>
        {/* Connection indicator */}
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,background:online?'#f0fdf4':'#fff5f5',border:`1px solid ${online?'#bbf7d0':'#fecaca'}`}}>
            {online
              ? <Wifi size={14} style={{color:'#16a34a'}}/>
              : <WifiOff size={14} style={{color:'#dc2626'}}/>}
            <span style={{fontSize:12,fontWeight:600,color:online?'#15803d':'#dc2626'}}>
              {online ? 'En linea' : 'Sin conexion'}
            </span>
          </div>
          {pendingCount>0&&(
            <button onClick={handleSync} disabled={!online||syncing}
              style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,background:'#fffbeb',border:'1px solid #fef3c7',cursor:online?'pointer':'not-allowed',fontSize:12,fontWeight:600,color:'#92400e'}}>
              <RefreshCw size={14} style={{animation:syncing?'spin 1s linear infinite':''}}/>
              {syncing?'Sincronizando...`':`${pendingCount} pendiente(s)`}
            </button>)}
        </div>
      </div>

      {/* Offline notice */}
      {!online&&(
        <div style={{padding:'10px 16px',background:'#fff5f5',border:'1px solid #fecaca',borderRadius:8,marginBottom:16,fontSize:13,color:'#dc2626',display:'flex',alignItems:'center',gap:10}}>
          <WifiOff size={16}/>
          <div>
            <strong>Modo sin conexion</strong> — La calculadora funciona normalmente. Los calculos se guardaran localmente y se sincronizaran cuando vuelva la conexion.
            {pendingCount>0&&<span style={{marginLeft:8,fontWeight:600}}>{pendingCount} calculo(s) pendiente(s) de sincronizar.</span>}
          </div>
        </div>)}

      {/* Panel flotante sticky */}
      <div style={{position:'sticky',top:0,zIndex:100,marginBottom:16}}>
        <div style={{background:'white',borderRadius:12,boxShadow:'0 4px 20px rgba(0,0,0,0.12)',border:'1px solid #e5e5e5',padding:'12px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:220}}>
            <select className="form-select" style={{marginBottom:0}} value={proyectoSel} onChange={e=>setProyectoSel(e.target.value)}>
              <option value="">Seleccionar proyecto para guardar calculos...</option>
              {proyectos.map(p=><option key={p.id} value={p.id}>{p.referencia} — {p.client?.empresa||p.clientId}</option>)}
            </select>
            {!online&&proyectos.length===0&&(
              <div style={{fontSize:11,color:'#888',marginTop:4}}>Sin conexion — los proyectos se cargaran cuando vuelva internet</div>)}
          </div>
          {proyectoSel&&(
            <button onClick={()=>setShowCarrito(!showCarrito)}
              style={{display:'flex',alignItems:'center',gap:8,padding:'10px 16px',borderRadius:10,border:'none',cursor:'pointer',fontWeight:600,fontSize:13,background:showCarrito?'#7B0D0D':'#f0fdf4',color:showCarrito?'white':'#15803d',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',flexShrink:0}}>
              <ShoppingBag size={16}/>
              Carpeta: {calculos.length}
              {calculos.filter(c=>c._local||c._pendingSync).length>0&&
                <span style={{background:'#fef3c7',color:'#92400e',borderRadius:10,padding:'1px 6px',fontSize:11,marginLeft:4}}>
                  {calculos.filter(c=>c._local||c._pendingSync).length} local(es)
                </span>}
              {showCarrito?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
            </button>)}
        </div>
      </div>

      {/* Carpeta desplegable */}
      {showCarrito&&proyectoSel&&(
        <div className="card" style={{marginBottom:16,border:'2px solid #bbf7d0'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <h3 className="card-title">
              Carpeta — {proyectoActual?.referencia||'Proyecto'}
              {!online&&<span style={{fontSize:11,color:'#888',marginLeft:8,fontWeight:400}}>(modo offline)</span>}
            </h3>
            <button onClick={()=>setShowCarrito(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#888'}}><X size={16}/></button>
          </div>
          {calculos.length===0?(
            <div style={{textAlign:'center',padding:20,color:'#aaa',fontSize:13}}>Sin calculos aun.</div>
          ):(
            <div style={{overflowX:'auto'}}>
              <table style={{minWidth:700}}>
                <thead><tr><th>Producto</th><th>Superficie</th><th>Area m2</th><th>Micras</th><th>Litros</th><th>Unidades</th><th>V. Unit.</th><th>Estado</th><th></th></tr></thead>
                <tbody>{calculos.map(c=>(
                  <tr key={c.id}>
                    <td><div style={{fontWeight:600,fontSize:12}}>{c.productoNombre}</div><div style={{fontSize:11,color:'#7B0D0D'}}>{c.productoCodigo}</div></td>
                    <td style={{fontSize:12}}>{c.superficieLabel}</td>
                    <td style={{fontWeight:600}}>{parseFloat(c.areaM2).toFixed(4)}</td>
                    <td>{c.micras} um</td>
                    <td style={{color:'#2563eb',fontWeight:600}}>{parseFloat(c.litrosTotales).toFixed(2)} L</td>
                    <td style={{fontWeight:700}}>{Math.ceil(parseFloat(c.unidades||0))}</td>
                    <td>${parseFloat(c.vUnitario||0).toFixed(2)}</td>
                    <td>
                      {(c._local||c._pendingSync)
                        ? <span style={{fontSize:11,color:'#d97706',background:'#fffbeb',padding:'2px 6px',borderRadius:8}}>Pendiente sync</span>
                        : <span style={{fontSize:11,color:'#15803d',background:'#f0fdf4',padding:'2px 6px',borderRadius:8}}>Guardado</span>}
                    </td>
                    <td style={{display:'flex',gap:4}}>
                      <button onClick={()=>setVerCalculo(c)} style={{background:'none',border:'none',cursor:'pointer',color:'#2563eb',padding:4}} title="Ver detalle del calculo"><Eye size={14}/></button>
                      <button onClick={()=>handleEliminar(c.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',padding:4}}><Trash2 size={14}/></button>
                    </td>
                  </tr>))}</tbody>
              </table>
            </div>)}
        </div>)}

      {/* Layout principal 2 columnas */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,alignItems:'start'}}>

        {/* Columna izquierda */}
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div className="card" style={{padding:'14px'}}>
            <div style={{fontSize:13,fontWeight:600,color:'#555',marginBottom:10}}>Tipo de superficie</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:catSel?12:0}}>
              {CATEGORIAS.map(cat=>(
                <button key={cat.id} style={tabStyle(catSel===cat.id)} onClick={()=>handleCat(cat.id)}>
                  {cat.label}
                </button>))}
            </div>
            {catActual&&(<>
              <div style={{fontSize:12,fontWeight:600,color:'#888',marginBottom:8,marginTop:4}}>Geometria</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:tipoSel?12:0}}>
                {catActual.tipos.map(t=>(
                  <button key={t.id} onClick={()=>handleTipo(t.id)}
                    style={{padding:'6px 10px',border:`2px solid ${tipoSel===t.id?'#7B0D0D':'#e5e5e5'}`,borderRadius:8,background:tipoSel===t.id?'#fff5f5':'white',cursor:'pointer',fontSize:11,fontWeight:tipoSel===t.id?600:400,color:tipoSel===t.id?'#7B0D0D':'#555'}}>
                    {t.label}
                  </button>))}
              </div>
            </>)}
            {tipoSel&&(<>
              <div style={{fontSize:12,fontWeight:600,color:'#888',marginBottom:10}}>Medidas — {tipoActual?.label}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                {inputsDef.map(inp=>inp.tipo==='check'?(
                  <label key={inp.k} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,gridColumn:'1/-1'}}>
                    <input type="checkbox" checked={vals[inp.k]!==false} onChange={e=>setVal(inp.k,e.target.checked)} style={{width:16,height:16,accentColor:'#7B0D0D'}}/>
                    {inp.l}
                  </label>
                ):(
                  <div key={inp.k} className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:12}}>{inp.l}</label>
                    <input className="form-input" type="number" step="any" min="0" value={vals[inp.k]??''} onChange={e=>setVal(inp.k,e.target.value)}/>
                  </div>))}
              </div>
              <button className="btn btn-primary" style={{width:'100%',justifyContent:'center',marginTop:14}} onClick={handleCalc}>
                <Calculator size={16}/> Calcular area
              </button>
            </>)}
          </div>
        </div>

        {/* Columna derecha */}
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {resArea?(
            <div className="card" style={{borderTop:'4px solid #7B0D0D'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:700,color:'#555'}}>{tipoActual?.label}</div>
                <button onClick={()=>setShowDet(!showDet)} style={{background:'none',border:'1px solid #e5e5e5',borderRadius:6,padding:'3px 10px',cursor:'pointer',fontSize:12,color:'#666'}}>
                  {showDet?'Ocultar':'Desglose'}
                </button>
              </div>
              <div style={{padding:'12px',background:'#fff5f5',borderRadius:10,border:'2px solid #fecaca',textAlign:'center',marginBottom:10}}>
                <div style={{fontSize:11,color:'#888'}}>Area total</div>
                <div style={{fontSize:44,fontWeight:800,color:'#7B0D0D',lineHeight:1}}>{area.toFixed(4)}</div>
                <div style={{fontSize:14,color:'#7B0D0D',fontWeight:600}}>m2</div>
              </div>
              {resArea.extra&&(
                <div style={{padding:'8px 12px',background:'#eff6ff',borderRadius:8,border:'1px solid #bfdbfe',marginBottom:8,fontSize:12}}>
                  <strong style={{color:'#1d4ed8'}}>{resArea.extra.label}:</strong> {resArea.extra.value}
                </div>)}
              {showDet&&resArea.detalles.map((d,i)=>(
                <div key={i} style={{fontSize:11,color:'#666',padding:'3px 0',borderBottom:'1px solid #f0f0f0'}}>{d}</div>))}
            </div>
          ):(
            <div style={{background:'white',borderRadius:12,padding:32,textAlign:'center',color:'#ddd',boxShadow:'var(--sombra)'}}>
              <Calculator size={40} style={{opacity:0.3,marginBottom:8}}/>
              <p style={{fontSize:13}}>Selecciona superficie y calcula el area</p>
            </div>)}

          {resArea&&(
            <div className="card">
              <div style={{fontSize:13,fontWeight:700,color:'#555',marginBottom:12}}>Producto a aplicar</div>
              <div style={{position:'relative',marginBottom:10}}>
                <input className="form-input" placeholder="Buscar producto..."
                  value={searchProd} onChange={e=>{setSearchProd(e.target.value);setShowProdList(true);}}
                  onFocus={()=>setShowProdList(true)}/>
                {showProdList&&(
                  <div style={{position:'absolute',top:'100%',left:0,right:0,background:'white',border:'1px solid #e5e5e5',borderRadius:8,maxHeight:220,overflowY:'auto',zIndex:100,boxShadow:'0 4px 16px rgba(0,0,0,0.12)'}}>
                    {prodOrd.slice(0,25).map(p=>{
                      const yaCalc=codigosCalc.has(p.codigo);
                      return(<div key={p.codigo}
                        onClick={()=>{
                          setProdSel(p);setShowProdList(false);setSearchProd(`${p.codigo} — ${p.producto}`);
                          if(p.precio!=null && p.precio!=='') setVUnitario(String(parseFloat(p.precio)||''));
                        }}
                        style={{padding:'7px 12px',cursor:'pointer',borderBottom:'1px solid #f0f0f0'}}
                        onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                        onMouseLeave={e=>e.currentTarget.style.background='white'}>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          {yaCalc&&<span style={{color:'#16a34a',fontSize:11,flexShrink:0}}>calculado</span>}
                          <span style={{fontSize:11,fontWeight:700,color:'#7B0D0D',flexShrink:0}}>{p.codigo}</span>
                          <span style={{fontSize:12,fontWeight:600}}>{p.producto}</span>
                          {p.presentacion&&<span style={{fontSize:11,color:'#888'}}>({p.presentacion})</span>}
                        </div>
                      </div>);})}
                    {prodOrd.length===0&&<div style={{padding:16,textAlign:'center',color:'#888',fontSize:12}}>Sin resultados</div>}
                  </div>)}
              </div>

              {prodSel&&(<>
                <div style={{padding:'10px 12px',background:'#f0f9ff',borderRadius:8,border:'1px solid #bae6fd',marginBottom:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:'#7B0D0D'}}>{prodSel.codigo}</div>
                      <div style={{fontSize:14,fontWeight:700}}>{prodSel.producto}</div>
                      <div style={{display:'flex',gap:8,marginTop:6,flexWrap:'wrap'}}>
                        <span style={{background:'#e0f2fe',padding:'2px 8px',borderRadius:10,color:'#0369a1',fontSize:11}}>Solidos: {((parseFloat(prodSel.solidos_pct)||1)*100).toFixed(0)}%</span>
                        <span style={{background:'#fef3c7',padding:'2px 8px',borderRadius:10,color:'#92400e',fontSize:11}}>Thinner: {((parseFloat(prodSel.thinner)||0)*100).toFixed(0)}%</span>
                      </div>
                    </div>
                    <button onClick={()=>{setProdSel(null);setSearchProd('');setVUnitario('');}} style={{background:'none',border:'none',cursor:'pointer',color:'#888',padding:4}}><X size={14}/></button>
                  </div>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:12}}>Espesor (micras)</label>
                    <input className="form-input" type="number" step="any" value={micras}
                      onChange={e=>setMicras(e.target.value===''?'':parseFloat(e.target.value))}
                      onBlur={e=>{ if(e.target.value==='') setMicras(250); }}/>
                    <span style={{fontSize:10,color:'#888',marginTop:2,display:'block'}}>250 um = 0.25 mm</span>
                  </div>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:12}}>Perdida — <strong style={{color:'#7B0D0D'}}>{perdida}%</strong></label>
                    <input type="range" min="0" max="40" value={perdida} onChange={e=>setPerdida(parseInt(e.target.value))} style={{width:'100%',accentColor:'#7B0D0D',marginTop:10}}/>
                  </div>
                </div>

                {area>0&&micras>0&&(
                  <div style={{background:'#fff5f5',borderRadius:10,padding:12,border:'2px solid #fecaca',marginBottom:12}}>
                    <div style={{fontSize:11,color:'#888',marginBottom:8}}>
                      (1000/{micras}um) x {((parseFloat(prodSel.solidos_pct)||1)*100).toFixed(0)}% = <strong>{rendimiento.toFixed(3)} m2/L</strong>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:6}}>
                      {[{v:litrosTot.toFixed(2),u:'litros'},{v:galones.toFixed(2),u:'galones'},{v:unidadesCeil,u:prodSel.presentacion||'unidades'}].map(m=>(
                        <div key={m.u} style={{textAlign:'center',padding:'8px 4px',background:'white',borderRadius:8}}>
                          <div style={{fontSize:22,fontWeight:800,color:'#7B0D0D'}}>{m.v}</div>
                          <div style={{fontSize:10,color:'#888'}}>{m.u}</div>
                        </div>))}
                    </div>
                  </div>)}

                <div style={{display:'flex',gap:10,alignItems:'flex-end'}}>
                  <div className="form-group" style={{margin:0,flex:1}}>
                    <label className="form-label" style={{fontSize:12}}>Precio unitario ($)</label>
                    <input className="form-input" type="number" step="0.01" value={vUnitario} onChange={e=>setVUnitario(e.target.value)} placeholder="0.00"/>
                  </div>
                  {proyectoSel?(
                    <button className="btn btn-primary" onClick={handleAgregar} disabled={saving||!area} style={{flexShrink:0}}>
                      <ShoppingBag size={15}/> {saving?'Guardando...':online?'Agregar':'Guardar offline'}
                    </button>
                  ):(
                    <div style={{fontSize:11,color:'#d97706',padding:'8px 10px',background:'#fffbeb',borderRadius:8,border:'1px solid #fef3c7',flexShrink:0}}>
                      Selecciona un proyecto arriba
                    </div>)}
                </div>
              </>)}
            </div>)}
        </div>
      </div>

      {/* Modal: Ver detalle del calculo */}
      {verCalculo&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'white',borderRadius:12,maxWidth:560,width:'100%',overflow:'hidden',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
            <div style={{background:'#7B0D0D',padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{color:'white',fontWeight:700,fontSize:15}}>Detalle del calculo</div>
              <button onClick={()=>setVerCalculo(null)} style={{background:'none',border:'none',cursor:'pointer',color:'white'}}><X size={18}/></button>
            </div>
            <div style={{padding:20}}>
              <div style={{marginBottom:14,padding:'10px 14px',background:'#f0f9ff',borderRadius:8,border:'1px solid #bae6fd'}}>
                <div style={{fontSize:11,fontWeight:700,color:'#7B0D0D'}}>{verCalculo.productoCodigo}</div>
                <div style={{fontSize:15,fontWeight:700}}>{verCalculo.productoNombre}</div>
                <div style={{fontSize:12,color:'#888'}}>{verCalculo.productoMarca} · {verCalculo.presentacion}</div>
              </div>
              {/* Input values from calculation */}
              {verCalculo.inputValues && (() => {
                try {
                  const inputs = JSON.parse(verCalculo.inputValues);
                  const labels = {largo:'Largo (m)',ancho:'Ancho (m)',alto:'Alto (m)',
                    diametro:'Diametro (m)',radio:'Radio (m)',cantidad:'N° de piezas',
                    altura:'Altura (m)',base:'Base (m)',litros:'Volumen (litros)',
                    l1:'Lado largo L1 (m)',l2:'Lado corto L2 (m)',a:'Semi-eje A (m)',
                    b:'Semi-eje B (m)',angulo:'Angulo (°)',espejos:'N° tapas',
                    caras:'N° caras',diametro_pulg:'Diametro (pulg)',
                    largo_pies:'Largo (pies)',anchoAnillo:'Ancho anillo (m)',
                    anchoImper:'Extension impermeabilizante (m)'};
                  const entries = Object.entries(inputs).filter(([k,v])=>v!==undefined&&v!==''&&v!==null&&k!=='conTecho'&&k!=='interior'&&k!=='conBase');
                  if (entries.length === 0) return null;
                  return (
                    <div style={{marginBottom:14,padding:'10px 12px',background:'#f8faff',borderRadius:8,border:'1px solid #bfdbfe'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#1d4ed8',marginBottom:8}}>Valores ingresados en la calculadora</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                        {entries.map(([k,v])=>(
                          <div key={k} style={{fontSize:12}}>
                            <span style={{color:'#888'}}>{labels[k]||k}: </span>
                            <strong>{typeof v==='boolean'?(v?'Si':'No'):v}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } catch(e) { return null; }
              })()}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                {[
                  {l:'Tipo de superficie',v:verCalculo.superficieLabel},
                  {l:'Area calculada',v:`${parseFloat(verCalculo.areaM2).toFixed(4)} m²`},
                  {l:'Espesor de pelicula',v:`${verCalculo.micras} µm`},
                  {l:'% Solidos del producto',v:`${((parseFloat(verCalculo.solidosPct)||1)*100).toFixed(0)}%`},
                  {l:'% Thinner',v:`${((parseFloat(verCalculo.thinnerPct)||0)*100).toFixed(0)}%`},
                  {l:'% Perdida/desperdicio',v:`${verCalculo.perdidaPct}%`},
                  {l:'Rendimiento calculado',v:`${parseFloat(verCalculo.rendimientoM2L).toFixed(3)} m²/L`},
                  {l:'Litros sin perdida',v:`${parseFloat(verCalculo.litrosBase).toFixed(3)} L`},
                ].map(item=>(
                  <div key={item.l} style={{padding:'8px 12px',background:'#fafafa',borderRadius:8}}>
                    <div style={{fontSize:11,color:'#888',marginBottom:2}}>{item.l}</div>
                    <div style={{fontSize:14,fontWeight:600}}>{item.v}</div>
                  </div>
                ))}
              </div>
              <div style={{padding:'12px 16px',background:'#fff5f5',borderRadius:10,border:'2px solid #fecaca'}}>
                <div style={{fontSize:12,color:'#888',marginBottom:8}}>Resultado final (con {verCalculo.perdidaPct}% de perdida)</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                  {[
                    {v:`${parseFloat(verCalculo.litrosTotales).toFixed(2)}`,u:'litros totales'},
                    {v:`${parseFloat(verCalculo.galones).toFixed(2)}`,u:'galones'},
                    {v:`${Math.ceil(parseFloat(verCalculo.unidades||0))}`,u:verCalculo.unidadMedida||'unidades'},
                  ].map(m=>(
                    <div key={m.u} style={{textAlign:'center',padding:'8px',background:'white',borderRadius:8}}>
                      <div style={{fontSize:20,fontWeight:800,color:'#7B0D0D'}}>{m.v}</div>
                      <div style={{fontSize:10,color:'#888'}}>{m.u}</div>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:11,color:'#555',marginTop:10,textAlign:'center'}}>
                  Formula: (1000 ÷ {verCalculo.micras}µm) × {((parseFloat(verCalculo.solidosPct)||1)*100).toFixed(0)}% = {parseFloat(verCalculo.rendimientoM2L).toFixed(3)} m²/L
                </div>
              </div>
            </div>
            <div style={{padding:'12px 20px',borderTop:'1px solid #eee',display:'flex',justifyContent:'flex-end'}}>
              <button className="btn btn-secondary" onClick={()=>setVerCalculo(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @media(max-width:768px){
          .calc-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </div>
  );
}
